import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Spinner,
  Select,
  useColorModeValue as mode,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import { startOfWeek, endOfWeek, addWeeks, format, differenceInCalendarWeeks } from "date-fns";
import { enGB } from "date-fns/locale";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(value || 0);

const RevenueStatsScreen = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [chartType, setChartType] = useState("weekly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    const fetchRevenueStats = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await axios.get(`/api/jobs/revenue-stats?year=${selectedYear}`);

        // Process weekly data - match backend results to frontend week slots
        const firstWeekStart = startOfWeek(new Date(selectedYear, 0, 1), { locale: enGB });
        const lastWeekEnd = endOfWeek(new Date(selectedYear, 11, 31), { locale: enGB });
        const weeks = [];
        let cursor = firstWeekStart;
        let weekCounter = 1;
        while (cursor <= lastWeekEnd) {
          const weekStart = new Date(cursor);
          const weekEnd = endOfWeek(weekStart, { locale: enGB });
          weeks.push({ weekIndex: weekCounter, weekStart, weekEnd });
          cursor = addWeeks(cursor, 1);
          weekCounter += 1;
        }

        // Process weekly data - map backend rows to week slots based on START_DATE
        // Filter to only include data from the selected year
        const yearEndDate = new Date(selectedYear, 11, 31);
        const filteredWeeklyData = (data.weekly || []).filter((row) => {
          if (!row.START_DATE) return false;
          const startDate = new Date(row.START_DATE);
          return startDate <= yearEndDate;
        });

        const weekly = weeks.map((week) => {
          // Find the backend row whose START_DATE falls in this week
          const matchingRow = filteredWeeklyData.find((row) => {
            if (!row.START_DATE) return false;
            const startDate = new Date(row.START_DATE);
            const weekStartDate = new Date(week.weekStart.getFullYear(), week.weekStart.getMonth(), week.weekStart.getDate());
            const weekEndDate = new Date(week.weekEnd.getFullYear(), week.weekEnd.getMonth(), week.weekEnd.getDate());
            const invoiceDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            // Check if invoice date falls within this week
            return invoiceDate >= weekStartDate && invoiceDate <= weekEndDate;
          });

          if (matchingRow) {
            const cash = Number(matchingRow.CASH) || 0;
            const cards = Number(matchingRow.CARDS) || 0;
            const cheque = Number(matchingRow.CHEQUE) || 0;
            const bank = Number(matchingRow.BANK) || 0;
            const moto = Number(matchingRow.MOTO) || 0;
            const ttotal = Number(matchingRow.TTOTAL) || 0;
            const revenue = ttotal || cash + cards + cheque + bank + moto;

            return {
              week: `Week ${week.weekIndex}`,
              weekStart: week.weekStart,
              weekEnd: week.weekEnd,
              revenue,
              cash,
              cards,
              cheque,
              bank,
              moto,
            };
          }

          // No data for this week
          return {
            week: `Week ${week.weekIndex}`,
            weekStart: week.weekStart,
            weekEnd: week.weekEnd,
            revenue: 0,
            cash: 0,
            cards: 0,
            cheque: 0,
            bank: 0,
            moto: 0,
          };
        });

        const monthly = (data.monthly || []).map((row) => {
          const monthIndex = Number(row.MONTH_INDEX) || 0;
          const monthName = monthIndex
            ? new Date(selectedYear, monthIndex - 1).toLocaleString("en-AU", { month: "long" })
            : "";

          const cash = Number(row.CASH) || 0;
          const cards = Number(row.CARDS) || 0;
          const cheque = Number(row.CHEQUE) || 0;
          const bank = Number(row.BANK) || 0;
          const moto = Number(row.MOTO) || 0;
          const ttotal = Number(row.TTOTAL) || 0;
          const revenue = ttotal || cash + cards + cheque + bank + moto;

          return {
            month: monthName,
            revenue,
            cash,
            cards,
            cheque,
            bank,
            moto,
          };
        });

        setWeeklyData(weekly);
        setMonthlyData(monthly);
      } catch (err) {
        console.error("Error fetching revenue stats", err);
        setError("Error fetching revenue data");
        setWeeklyData([]);
        setMonthlyData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRevenueStats();
  }, [selectedYear]);

  const getChartData = () => {
    const data = chartType === "weekly" ? weeklyData : monthlyData;
    const labels = data.map((d) => (chartType === "weekly" ? d.week : d.month));

    return {
      series: [
        {
          name: "Cash",
          data: data.map((d) => d.cash),
        },
        {
          name: "Cards",
          data: data.map((d) => d.cards),
        },
        {
          name: "Cheque",
          data: data.map((d) => d.cheque),
        },
        {
          name: "Bank",
          data: data.map((d) => d.bank),
        },
        {
          name: "MOTO",
          data: data.map((d) => d.moto),
        },
      ],
      options: {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: {
            show: true,
          },
        },
        colors: ["#48bb78", "#4299e1", "#b7791f", "#ed8936", "#ed64a6"],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 1,
          colors: ["#fff"],
        },
        xaxis: {
          categories: labels,
        },
        yaxis: {
          labels: {
            formatter: (value) => formatCurrency(value),
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value) => formatCurrency(value),
          },
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const total = series.reduce((sum, s) => sum + (s[dataPointIndex] || 0), 0);
            const label = w.globals.labels[dataPointIndex];
            const seriesNames = ["Cash", "Cards", "Cheque", "Bank", "MOTO"];
            const colors = ["#48bb78", "#4299e1", "#b7791f", "#ed8936", "#ed64a6"];

            let breakdown = "";
            series.forEach((s, idx) => {
              const value = s[dataPointIndex] || 0;
              if (value > 0) {
                breakdown += `
                  <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:3px;">
                    <div style="display:flex;align-items:center;gap:6px;">
                      <div style="width:10px;height:10px;border-radius:2px;background:${colors[idx]};"></div>
                      <span>${seriesNames[idx]}:</span>
                    </div>
                    <span style="font-weight:600;">${formatCurrency(value)}</span>
                  </div>
                `;
              }
            });

            return `
              <div style="padding:10px 12px;min-width:200px;">
                <div style="font-weight:600;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #e2e8f0;">${label}</div>
                ${breakdown}
                <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:8px;padding-top:6px;border-top:1px solid #e2e8f0;">
                  <span style="font-weight:600;">Total:</span>
                  <span style="font-weight:700;color:#2f855a;font-size:15px;">${formatCurrency(total)}</span>
                </div>
              </div>
            `;
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
      },
    };
  };

  const tableData = chartType === "weekly" ? weeklyData : monthlyData;
  const chartData = getChartData();

  const totalRevenue = tableData.reduce((sum, row) => sum + row.revenue, 0);
  const tableTotals = tableData.reduce(
    (totals, row) => ({
      revenue: totals.revenue + (row.revenue || 0),
      cash: totals.cash + (row.cash || 0),
      cards: totals.cards + (row.cards || 0),
      cheque: totals.cheque + (row.cheque || 0),
      bank: totals.bank + (row.bank || 0),
      moto: totals.moto + (row.moto || 0),
    }),
    {
      revenue: 0,
      cash: 0,
      cards: 0,
      cheque: 0,
      bank: 0,
      moto: 0,
    }
  );
  const currentYear = new Date().getFullYear();
  const isCurrentYear = selectedYear === currentYear;
  // Count completed weeks in the current year using the same locale boundaries as the data
  const weeksPassed =
    differenceInCalendarWeeks(
      startOfWeek(new Date(), { locale: enGB }),
      startOfWeek(new Date(selectedYear, 0, 1), { locale: enGB }),
      { locale: enGB }
    ) + 1;
  const monthsPassed = new Date().getMonth() + 1;
  const periodsUsed = isCurrentYear
    ? chartType === "weekly"
      ? Math.min(tableData.length, weeksPassed)
      : Math.min(tableData.length, monthsPassed)
    : tableData.length;
  const averageRevenue = totalRevenue / Math.max(1, periodsUsed);

  const bgColor = mode("white", "gray.800");
  const borderColor = mode("gray.200", "gray.700");
  const headerBg = mode("gray.50", "gray.700");
  const rowHoverBg = mode("gray.100", "gray.600");
  const tableDataBorder = mode("blue.200", "blue.700");
  const tableDataBg = mode("blue.50", "blue.900");

  return (
    <Box maxW='7xl' mx='auto' px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <VStack align='stretch' spacing={8}>
        {/* Header */}
        <Box>
          <Heading size='lg' mb={2}>
            Revenue Statistics
          </Heading>
          <Text color='gray.600' fontSize='sm'>
            Weekly and monthly revenue breakdown for {selectedYear}
          </Text>
        </Box>

        {/* Controls */}
        <Flex gap={4} direction={{ base: "column", md: "row" }} align='center'>
          <Box minW='150px'>
            <Text fontWeight='bold' mb={2} fontSize='sm'>
              Select Year
            </Text>
            <Select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </Box>

          <HStack spacing={2}>
            <Button
              variant={chartType === "weekly" ? "solid" : "outline"}
              colorScheme='blue'
              size='sm'
              onClick={() => setChartType("weekly")}
            >
              Weekly
            </Button>
            <Button
              variant={chartType === "monthly" ? "solid" : "outline"}
              colorScheme='blue'
              size='sm'
              onClick={() => setChartType("monthly")}
            >
              Monthly
            </Button>
          </HStack>
        </Flex>

        {/* Chart */}
        <Box bg={bgColor} borderRadius='lg' borderWidth='1px' borderColor={borderColor} p={6} shadow='md'>
          {loading ? (
            <Flex justify='center' align='center' h='300px'>
              <Spinner size='lg' />
            </Flex>
          ) : (
            <ReactApexChart
              type={chartData.options.chart.type}
              series={chartData.series}
              options={chartData.options}
              height={400}
            />
          )}
        </Box>

        {/* Summary */}
        {tableData.length > 0 && (
          <Box
            bg={tableDataBg}
            borderRadius='lg'
            borderWidth='1px'
            borderColor={tableDataBorder}
            p={6}
          >
            <Stack spacing={3}>
              <Heading size='md'>Year Summary - {selectedYear}</Heading>
              <HStack spacing={8} wrap='wrap'>
                <Box>
                  <Text fontSize='sm' color='gray.600' mb={1}>
                    Total Revenue
                  </Text>
                  <Text fontSize='2xl' fontWeight='bold' color='green.600'>
                    {formatCurrency(totalRevenue)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize='sm' color='gray.600' mb={1}>
                    Average {chartType === "weekly" ? "Weekly" : "Monthly"}
                  </Text>
                  <Text fontSize='2xl' fontWeight='bold' color='blue.600'>
                    {formatCurrency(averageRevenue)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize='sm' color='gray.600' mb={1}>
                    Periods
                  </Text>
                  <Text fontSize='2xl' fontWeight='bold'>
                    {periodsUsed}
                  </Text>
                </Box>
              </HStack>
            </Stack>
          </Box>
        )}

        {/* Table */}
        <Box bg={bgColor} borderRadius='lg' borderWidth='1px' borderColor={borderColor} shadow='md' overflowX='auto'>
          <Table variant='simple' size='sm'>
            <Thead bg={headerBg}>
              <Tr>
                <Th>{chartType === "weekly" ? "Week (Start - End)" : "Month"}</Th>
                <Th isNumeric>Revenue</Th>
                <Th isNumeric>Cash</Th>
                <Th isNumeric>Cards</Th>
                <Th isNumeric>Cheque</Th>
                <Th isNumeric>Bank</Th>
                <Th isNumeric>MOTO</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={7} textAlign='center' py={8}>
                    <Spinner size='md' />
                  </Td>
                </Tr>
              ) : tableData.length === 0 ? (
                <Tr>
                  <Td colSpan={7} textAlign='center' py={8}>
                    <Text color='gray.500'>No data available for {selectedYear}</Text>
                  </Td>
                </Tr>
              ) : (
                <>
                  {tableData.map((row, idx) => (
                    <Tr key={idx} _hover={{ bg: rowHoverBg }}>
                      <Td fontWeight='medium'>
                        {chartType === "weekly"
                          ? row.weekStart && row.weekEnd
                            ? `${row.week} (${format(new Date(row.weekStart), "dd/MM")} - ${format(
                                new Date(row.weekEnd),
                                "dd/MM"
                              )})`
                            : row.week
                          : row.month}
                      </Td>
                      <Td isNumeric fontWeight='bold' color='green.600'>
                        {formatCurrency(row.revenue)}
                      </Td>
                      <Td isNumeric>{formatCurrency(row.cash)}</Td>
                      <Td isNumeric>{formatCurrency(row.cards)}</Td>
                      <Td isNumeric>{formatCurrency(row.cheque)}</Td>
                      <Td isNumeric>{formatCurrency(row.bank)}</Td>
                      <Td isNumeric>{formatCurrency(row.moto)}</Td>
                    </Tr>
                  ))}
                  <Tr bg={headerBg}>
                    <Td fontWeight='bold'>Totals</Td>
                    <Td isNumeric fontWeight='bold' color='green.600'>
                      {formatCurrency(tableTotals.revenue)}
                    </Td>
                    <Td isNumeric fontWeight='bold'>{formatCurrency(tableTotals.cash)}</Td>
                    <Td isNumeric fontWeight='bold'>{formatCurrency(tableTotals.cards)}</Td>
                    <Td isNumeric fontWeight='bold'>{formatCurrency(tableTotals.cheque)}</Td>
                    <Td isNumeric fontWeight='bold'>{formatCurrency(tableTotals.bank)}</Td>
                    <Td isNumeric fontWeight='bold'>{formatCurrency(tableTotals.moto)}</Td>
                  </Tr>
                </>
              )}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </Box>
  );
};

export default RevenueStatsScreen;
